import checkExternalToken from '@/api/auth/checkExternalToken.endpoint';
import checkTaskTemplateAndThemeForClasses from '@/api/classes/checkTaskTemplateAndThemeForClasses.endpoint';
import getTeachersClasses from '@/api/classes/getTeachersClasses.endpoint';
import createTaskFromTemplate from '@/api/tasks/createTaskFromTemplate.endpoint';
import Dates from '@/constants/Dates';
import useCustomHook from '@/hooks/useCustomHook';
import openAPIErrorNotification from '@/shared/APIErrorNotification/APIErrorNotification';
import { GlobalContext } from '@/shared/GlobalContext/GlobalContext';
import { NavigationContext } from '@/shared/NavigationContext/NavigationContext';
import { SSOContext } from '@/shared/SSOContext/SSOContext';
import { notification } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useContext } from 'react';
import { deepClone } from 'src/modules/objectUtils';

export default function useAssignTaskDrawer({ taskTemplate, onFinish }) {
  const context = useContext(GlobalContext);
  const navigationContext = useContext(NavigationContext);
  const ssoContext = useContext(SSOContext);

  const { state, actions } = useCustomHook({
    defaultState: {
      loading: true,
      classes: [],
      selectedClasses: [],
      active: true,
      name: taskTemplate?.name,
      activationDate: new Date(),
      dueDate: null,
      processing: false,
      allowsOverdue: false,
      selectedStudents: [],
      classesAlreadyAssigned: [],
      selectedClassesAlreadyAssigned: [],
      pendingAssign: false,
      changeStudents: false,
      changeActivities: false,
      taskActivities: [],
      isSomeGoogleClass: false,
      sendToGoogle: true,
      googleMaxPoints: 100,
    },
    actions: {
      loadClasses: async () => {
        actions.setState({
          loading: true,
        });
        try {
          const classes = await getTeachersClasses({
            teacherId: context.state.user?.teacher?.id,
          });

          let classesAlreadyAssigned = [];
          if (classes.length) {
            const themeInClass = await checkTaskTemplateAndThemeForClasses(
              classes.map((clazz) => clazz.id),
              taskTemplate.themeId
            );
            classesAlreadyAssigned = getClassesAlreadyAssigned({
              classes,
              themeInClass,
              taskTemplate,
            });
          }

          actions.setState({
            classes: classes.sort((a, b) => a.name.localeCompare(b.name)),
            classesAlreadyAssigned,
            loading: false,
          });
        } catch (e) {
          console.log(e);
          actions.setState({ loading: false });
          openAPIErrorNotification({
            title: 'There was an error',
            description: 'There was a problem loading the classes',
            errorFeedback: e.feedbackData,
          });
        }
      },
      onClassAdded: (_, clazz) => {
        const selectedClasses = deepClone(state.selectedClasses);
        selectedClasses.push(clazz);
        actions.setState({ selectedClasses, changeStudents: false });
      },
      onClassRemoved: (classId) => {
        const selectedClasses = state.selectedClasses.filter(
          (clazz) => clazz.id != classId
        );
        actions.setState({ selectedClasses, changeStudents: false });
      },
      setName: (name) => {
        actions.setState({ name });
      },
      setActivationDate: (activationDate) => {
        actions.setState({ activationDate });
      },
      setDueDate: (dueDate) => {
        actions.setState({ dueDate });
      },
      setAllowsOverdue: (allowsOverdue) => {
        actions.setState({ allowsOverdue });
      },
      setChangeStudents: (changeStudents) => {
        actions.setState({ changeStudents });
      },
      setChangeActivities: (changeActivities) => {
        actions.setState({ changeActivities });
      },
      setSelectedStudents: (selectedStudents) => {
        actions.setState({ selectedStudents });
      },
      setTaskActivities: (taskActivities) => {
        actions.setState({ taskActivities });
      },
      setSendToGoogle: (sendToGoogle) => {
        actions.setState({ sendToGoogle });
      },
      setGoogleMaxPoints: (googleMaxPoints) => {
        actions.setState({ googleMaxPoints });
      },
      assign: async () => {
        actions.setState({
          processing: true,
        });
        const isGoogleClass = state.isSomeGoogleClass && state.sendToGoogle;
        try {
          if (isGoogleClass) {
            await checkExternalToken('google');
          }
          const taskData = prepareTaskDataFromTemplate(state, taskTemplate);
          await createTaskFromTemplate(taskData);
          actions.setState({
            processing: false,
          });
          notification.success({
            message: 'Task assigned to class/es',
            top: 60,
          });
          onFinish();
        } catch (e) {
          if (e?.response?.status == 428) {
            openAPIErrorNotification({
              title: 'There was an error',
              description: 'Your integration needs to be synced',
              needSync: true,
              errorFeedback: {},
            });
            actions.setState({ processing: false });
          } else if (
            isGoogleClass &&
            e?.response?.status == 500 &&
            e?.response?.data?.message == 'Request failed with status code 400'
          ) {
            actions.setState({ processing: false });
            openAPIErrorNotification({
              title: 'There was an error assigning this task',
              errorFeedback: {
                message: (
                  <>
                    It couldn't be synchronized with{' '}
                    <strong>Google Classroom</strong>. Please, be sure that the
                    class is not <strong>archived</strong> and you have
                    permissions to create tasks in it.
                  </>
                ),
              },
            });
          } else {
            actions.setState({ processing: false });
            openAPIErrorNotification({
              title: 'There was an error',
              description: 'There was a problem assigning this task',
              errorFeedback: e.feedbackData,
            });
          }
        }
      },
    },
  });

  useEffect(() => {
    if (context.state.loaded && context.state.user?.teacher) {
      actions.loadClasses();
    }
  }, [context.state.loaded]);

  useEffect(() => {
    const selectedClasses = state.classes.filter((clazz) =>
      navigationContext.state.classes.some(
        (navigationClass) => navigationClass.id == clazz.id
      )
    );
    actions.setState({ selectedClasses });
  }, [navigationContext.state.classes, state.classes]);

  useEffect(() => {
    const selectedClassesAlreadyAssigned = state.classesAlreadyAssigned.filter(
      (classAlreadyAssigned) =>
        state.selectedClasses.some(
          (selectedClass) => selectedClass.id == classAlreadyAssigned.id
        )
    );
    const isSomeGoogleClass = state.selectedClasses.some(
      (clazz) => clazz.provider == 'google'
    );
    actions.setState({ selectedClassesAlreadyAssigned, isSomeGoogleClass });
  }, [state.classesAlreadyAssigned, state.selectedClasses]);

  useEffect(() => {
    if (ssoContext.state.loaded) {
      if (ssoContext.state.pendingRedirect?.action == 'assign') {
        const savedState = {
          ...ssoContext.state.pendingRedirect.state,
          pendingAssign: true,
        };
        actions.setState(savedState);
        ssoContext.actions.clearPendingRedirect();
      }
    }
  }, [ssoContext.state.loaded]);

  useEffect(() => {
    if (state.pendingAssign) {
      actions.setState({ pendingAssign: false });
      actions.assign();
    }
  }, [state.pendingAssign]);

  return { state, actions };
}

function prepareTaskDataFromTemplate(state, taskTemplate) {
  const allStudents = !state.changeStudents || state.selectedClasses.length > 1;
  return {
    classes: state.selectedClasses.map((selectedClass) => ({
      id: selectedClass.id,
      studentIds: allStudents
        ? 'all'
        : state.selectedStudents.map((student) => student.id),
    })),
    taskTemplateId: taskTemplate.id,
    attributes: {
      name: state.name,
      active: state.active,
      activationDate: dayjs(state.activationDate).format(Dates.apiDateFormat),
      dueDate: state.dueDate
        ? dayjs(state.dueDate).format(Dates.apiDateFormat)
        : null,
      allowsOverdue: state.allowsOverdue,
      data: {
        sendToGoogle: state.isSomeGoogleClass ? state.sendToGoogle : undefined,
        googleMaxPoints: state.isSomeGoogleClass
          ? state.googleMaxPoints
          : undefined,
      },
    },
    customTaskActivities: state.changeActivities
      ? state.taskActivities.map((taskActivity, index) => ({
          ...taskActivity,
          order: index + 1,
          activity: null,
        }))
      : null,
  };
}

function getClassesAlreadyAssigned({ classes, themeInClass, taskTemplate }) {
  return themeInClass?.classResults
    .filter((classResult) =>
      classResult.taskTemplatesEnabled.some(
        (taskTemplateEnabled) => taskTemplateEnabled == taskTemplate.id
      )
    )
    .map((classResult) =>
      classes.find((clazz) => clazz.id == classResult.classId)
    );
}
